/* eslint-disable no-template-curly-in-string */
import { useMemo } from 'react';
import { Extension } from '@codemirror/state';
import { autocompletion, Completion, CompletionContext, snippetCompletion } from '@codemirror/autocomplete';

// React component snippets
const reactSnippets: Completion[] = [
  snippetCompletion(
    'import React from \'react\';\n\nexport default function ${1:ComponentName}() {\n  return (\n    <div>\n      ${2}\n    </div>\n  );\n}',
    { label: 'rfc', detail: 'React Function Component', type: 'snippet' }
  ),
  snippetCompletion(
    'import React from \'react\';\n\nconst ${1:ComponentName} = () => {\n  return (\n    <div>\n      ${2}\n    </div>\n  );\n};\n\nexport default ${1:ComponentName};',
    { label: 'rafce', detail: 'React Arrow Function Component Export', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:ComponentName} = () => {\n  return (\n    <div>\n      ${2}\n    </div>\n  );\n};',
    { label: 'rafc', detail: 'React Arrow Function Component', type: 'snippet' }
  ),
  snippetCompletion(
    'export const ${1:ComponentName} = () => {\n  return (\n    <div>\n      ${2}\n    </div>\n  );\n};',
    { label: 'rafcex', detail: 'React Arrow Function Component Exported', type: 'snippet' }
  ),
  snippetCompletion(
    'import React, { memo } from \'react\';\n\nconst ${1:ComponentName} = memo(function ${1:ComponentName}() {\n  return (\n    <div>\n      ${2}\n    </div>\n  );\n});\n\nexport default ${1:ComponentName};',
    { label: 'rmemo', detail: 'React Memo Component', type: 'snippet' }
  ),
];

// React hooks snippets
const hookSnippets: Completion[] = [
  snippetCompletion(
    'const [${1:state}, set${2:State}] = useState(${3:initialValue});',
    { label: 'us', detail: 'useState', type: 'snippet' }
  ),
  snippetCompletion(
    'const [${1:state}, set${2:State}] = useState<${3:Type}>(${4:initialValue});',
    { label: 'ust', detail: 'useState with Type', type: 'snippet' }
  ),
  snippetCompletion(
    'useEffect(() => {\n  ${1}\n}, [${2}]);',
    { label: 'ue', detail: 'useEffect', type: 'snippet' }
  ),
  snippetCompletion(
    'useEffect(() => {\n  ${1}\n\n  return () => {\n    ${2}\n  };\n}, [${3}]);',
    { label: 'uec', detail: 'useEffect with Cleanup', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:value} = useMemo(() => {\n  return ${2};\n}, [${3}]);',
    { label: 'um', detail: 'useMemo', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:callback} = useCallback(${2:() => {\n  \n}}, [${3}]);',
    { label: 'ucb', detail: 'useCallback', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:ref} = useRef<${2:HTMLDivElement}>(${3:null});',
    { label: 'ur', detail: 'useRef', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:value} = useContext(${2:Context});',
    { label: 'uctx', detail: 'useContext', type: 'snippet' }
  ),
  snippetCompletion(
    'const [${1:state}, ${2:dispatch}] = useReducer(${3:reducer}, ${4:initialState});',
    { label: 'ured', detail: 'useReducer', type: 'snippet' }
  ),
  snippetCompletion(
    'useLayoutEffect(() => {\n  ${1}\n}, [${2}]);',
    { label: 'ule', detail: 'useLayoutEffect', type: 'snippet' }
  ),
];

// Import snippets
const importSnippets: Completion[] = [
  snippetCompletion(
    'import { ${2} } from \'${1}\';',
    { label: 'imp', detail: 'Import', type: 'snippet' }
  ),
  snippetCompletion(
    'import ${2} from \'${1}\';',
    { label: 'impd', detail: 'Import Default', type: 'snippet' }
  ),
  snippetCompletion(
    'import * as ${2} from \'${1}\';',
    { label: 'impa', detail: 'Import All', type: 'snippet' }
  ),
  snippetCompletion(
    'import React from \'react\';',
    { label: 'imr', detail: 'Import React', type: 'snippet' }
  ),
  snippetCompletion(
    'import React, { useState } from \'react\';',
    { label: 'imrs', detail: 'Import React useState', type: 'snippet' }
  ),
  snippetCompletion(
    'import React, { useState, useEffect } from \'react\';',
    { label: 'imrse', detail: 'Import React useState useEffect', type: 'snippet' }
  ),
];

// TypeScript snippets
const typescriptSnippets: Completion[] = [
  snippetCompletion(
    'interface ${1:Name} {\n  ${2}\n}',
    { label: 'int', detail: 'Interface', type: 'snippet' }
  ),
  snippetCompletion(
    'type ${1:Name} = {\n  ${2}\n};',
    { label: 'typ', detail: 'Type Alias', type: 'snippet' }
  ),
  snippetCompletion(
    'type ${1:Name}Props = {\n  ${2}\n};',
    { label: 'typp', detail: 'Props Type', type: 'snippet' }
  ),
  snippetCompletion(
    'export type ${1:Name} = {\n  ${2}\n};',
    { label: 'etyp', detail: 'Export Type', type: 'snippet' }
  ),
  snippetCompletion(
    'export interface ${1:Name} {\n  ${2}\n}',
    { label: 'eint', detail: 'Export Interface', type: 'snippet' }
  ),
];

// JSX snippets
const jsxSnippets: Completion[] = [
  snippetCompletion(
    '<div className="${1}">\n  ${2}\n</div>',
    { label: 'div', detail: 'div element', type: 'snippet' }
  ),
  snippetCompletion(
    '<span className="${1}">${2}</span>',
    { label: 'span', detail: 'span element', type: 'snippet' }
  ),
  snippetCompletion(
    '<p className="${1}">${2}</p>',
    { label: 'p', detail: 'p element', type: 'snippet' }
  ),
  snippetCompletion(
    '<button onClick={${1:handleClick}} className="${2}">\n  ${3:Click me}\n</button>',
    { label: 'btn', detail: 'button element', type: 'snippet' }
  ),
  snippetCompletion(
    '<input\n  type="${1:text}"\n  value={${2}}\n  onChange={${3}}\n  className="${4}"\n/>',
    { label: 'inp', detail: 'input element', type: 'snippet' }
  ),
  snippetCompletion(
    '<form onSubmit={${1:handleSubmit}}>\n  ${2}\n</form>',
    { label: 'form', detail: 'form element', type: 'snippet' }
  ),
  snippetCompletion(
    '<ul className="${1}">\n  {${2:items}.map((${3:item}) => (\n    <li key={${3:item}.${4:id}}>${5}</li>\n  ))}\n</ul>',
    { label: 'ulmap', detail: 'ul with map', type: 'snippet' }
  ),
  snippetCompletion(
    '{${1:condition} && (\n  ${2}\n)}',
    { label: 'cand', detail: 'Conditional && render', type: 'snippet' }
  ),
  snippetCompletion(
    '{${1:condition} ? (\n  ${2}\n) : (\n  ${3}\n)}',
    { label: 'ctern', detail: 'Conditional ternary render', type: 'snippet' }
  ),
  snippetCompletion(
    '<>\n  ${1}\n</>',
    { label: 'frag', detail: 'React Fragment', type: 'snippet' }
  ),
  snippetCompletion(
    '<React.Fragment>\n  ${1}\n</React.Fragment>',
    { label: 'rfrag', detail: 'React.Fragment', type: 'snippet' }
  ),
];

// Console snippets
const consoleSnippets: Completion[] = [
  snippetCompletion(
    'console.log(${1});',
    { label: 'cl', detail: 'console.log', type: 'snippet' }
  ),
  snippetCompletion(
    'console.log(\'${1}\', ${1});',
    { label: 'clv', detail: 'console.log with label', type: 'snippet' }
  ),
  snippetCompletion(
    'console.error(${1});',
    { label: 'ce', detail: 'console.error', type: 'snippet' }
  ),
  snippetCompletion(
    'console.warn(${1});',
    { label: 'cw', detail: 'console.warn', type: 'snippet' }
  ),
  snippetCompletion(
    'console.table(${1});',
    { label: 'ct', detail: 'console.table', type: 'snippet' }
  ),
];

// Function snippets
const functionSnippets: Completion[] = [
  snippetCompletion(
    'function ${1:name}(${2}) {\n  ${3}\n}',
    { label: 'fn', detail: 'Function', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:name} = (${2}) => {\n  ${3}\n};',
    { label: 'afn', detail: 'Arrow Function', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:name} = (${2}) => ${3};',
    { label: 'afns', detail: 'Arrow Function Short', type: 'snippet' }
  ),
  snippetCompletion(
    'async function ${1:name}(${2}) {\n  ${3}\n}',
    { label: 'afnc', detail: 'Async Function', type: 'snippet' }
  ),
  snippetCompletion(
    'const ${1:name} = async (${2}) => {\n  ${3}\n};',
    { label: 'aafn', detail: 'Async Arrow Function', type: 'snippet' }
  ),
];

// All snippets combined
const allSnippets: Completion[] = [
  ...reactSnippets,
  ...hookSnippets,
  ...importSnippets,
  ...typescriptSnippets,
  ...jsxSnippets,
  ...consoleSnippets,
  ...functionSnippets,
];

// Snippet completion source
function snippetSource(context: CompletionContext) {
  const word = context.matchBefore(/\w*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  return {
    from: word.from,
    options: allSnippets,
    validFor: /^\w*$/,
  };
}

// Get file extension
function getFileExtension(name: string): string {
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.slice(lastDot) : '';
}

// Get directory from a file path
function getDirectory(path: string): string {
  const lastSlash = path.lastIndexOf('/');
  return lastSlash > 0 ? path.slice(0, lastSlash) : '';
}

// Calculate relative path from source to target
function getRelativePath(fromPath: string, toPath: string): string {
  const fromDir = getDirectory(fromPath);
  const fromParts = fromDir.split('/').filter(Boolean);
  const toParts = toPath.split('/').filter(Boolean);

  let commonIndex = 0;
  while (
    commonIndex < fromParts.length &&
    commonIndex < toParts.length &&
    fromParts[commonIndex] === toParts[commonIndex]
  ) {
    commonIndex++;
  }

  const upCount = fromParts.length - commonIndex;
  const upPath = upCount > 0 ? '../'.repeat(upCount) : './';
  const downPath = toParts.slice(commonIndex).join('/');

  return upPath + downPath;
}

// Check if we're inside an import path string
function isInImportPath(line: string, pos: number): { inPath: boolean; start: number; prefix: string } {
  const importMatch = line.match(/(?:from\s+|import\s*\(|require\s*\()(['"])/);

  if (!importMatch) {
    return { inPath: false, start: 0, prefix: '' };
  }

  const quote = importMatch[1];
  const pathStart = importMatch.index! + importMatch[0].length;
  const pathEnd = line.indexOf(quote, pathStart);

  if (pos >= pathStart && (pathEnd === -1 || pos <= pathEnd)) {
    const prefix = line.slice(pathStart, pos);
    return { inPath: true, start: pathStart, prefix };
  }

  return { inPath: false, start: 0, prefix: '' };
}

type FileItem = { name: string; path: string; type: 'file' | 'folder' };

// Create path completion source
function createPathSource(fileList: FileItem[], activePath: string | null) {
  return function pathSource(context: CompletionContext) {
    const { state, pos } = context;
    const line = state.doc.lineAt(pos);
    const lineText = line.text;
    const linePos = pos - line.from;

    const { inPath, start, prefix } = isInImportPath(lineText, linePos);

    if (!inPath) {
      return null;
    }

    const prefixLower = prefix.toLowerCase();
    const options: Completion[] = [];

    for (const file of fileList) {
      if (file.type !== 'file') continue;

      const ext = getFileExtension(file.name).toLowerCase();
      if (!['.ts', '.tsx', '.js', '.jsx', '.json', '.css', '.scss'].includes(ext)) {
        continue;
      }

      let suggestedPath: string;

      if (activePath && (prefix.startsWith('./') || prefix.startsWith('../') || prefix === '')) {
        suggestedPath = getRelativePath(activePath, file.path);
      } else {
        suggestedPath = file.path;
      }

      if (['.ts', '.tsx', '.js', '.jsx'].includes(ext)) {
        suggestedPath = suggestedPath.replace(/\/index\.(tsx?|jsx?)$/, '');
        suggestedPath = suggestedPath.replace(/\.(tsx?|jsx?)$/, '');
      }

      if (prefixLower && !suggestedPath.toLowerCase().includes(prefixLower)) {
        continue;
      }

      options.push({
        label: suggestedPath,
        detail: file.path,
        type: 'file',
        boost: suggestedPath.startsWith(prefix) ? 1 : 0,
        apply: suggestedPath,
      });
    }

    options.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(prefixLower);
      const bStarts = b.label.toLowerCase().startsWith(prefixLower);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.label.localeCompare(b.label);
    });

    const limitedOptions = options.slice(0, 50);

    if (limitedOptions.length === 0) {
      return null;
    }

    return {
      from: line.from + start,
      to: pos,
      options: limitedOptions,
      validFor: /^[./\w-]*$/,
    };
  };
}

type UseSnippetsParams = {
  fileList?: FileItem[];
  activePath?: string | null;
};

export function useSnippets({ fileList = [], activePath = null }: UseSnippetsParams = {}) {
  const pathSource = useMemo(
    () => createPathSource(fileList, activePath),
    [fileList, activePath]
  );

  const snippetExtension = useMemo<Extension>(() => {
    return autocompletion({
      override: [snippetSource, pathSource],
      defaultKeymap: true,
      icons: true,
      optionClass: (completion) => {
        if (completion.type === 'snippet') return 'cm-snippet-completion';
        if (completion.type === 'file') return 'cm-path-completion';
        return '';
      },
    });
  }, [pathSource]);

  return { snippetExtension };
}
